import {FormikHelpers as FormikActions} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {NavLink, Link, useLocation, useNavigate} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {ToggleGroup} from '~/components/Controls/ToggleGroup';
import {onSubmitError} from '~/components/Form';
import {IModalComponent, IModalComponentClose, useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {TableSort, TableSortDirection} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {stringDateFormat, toUTCWithLocalTime} from '~/lib/date';
import {Copy, renderDollars} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {ResourceLoader} from '~/lib/loader';
import {ID} from '~/lib/model';
import {sortByInterval, sortByNumber, sortByString} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {fetchPaymentDetails, updatePaymentDetails} from '~/modules/Clients/api';
import {SelectSeller} from '~/modules/Clients/components/SelectSeller';
import {PaymentDetailsForm} from '~/modules/Clients/forms/PaymentDetailsForm';
import {IPaymentDetails} from '~/modules/Clients/models/PaymentDetails';
import {PaymentsAbility} from '~/modules/Payments/ability';
import { updatePaymentStatus} from '~/modules/Payments/api';
import {PaymentsTypeNavigation} from '~/modules/Payments/components/PaymentsTypeNavigation';
import {PaymentStatusMenu} from '~/modules/Payments/components/PaymentStatusMenu';
import {getSellerId, paymentsLinks} from '~/modules/Payments/links';
import {PAYMENTS_QUERY_KEY, usePaymentsQuery} from '~/modules/Payments/paymentsQuery';
import {
    defaultPayment,
    formatInterval,
    IPayment,
    PaymentStatus,
} from '~/modules/Payments/models/Payment';
import style from './PaymentsView.module.scss';

interface IPaymentDetailsOverviewProps {
    sellerId?: ID;
    readonly?: boolean;
    onSubmit?: (values: IPaymentDetails, formikActions: FormikActions<IPaymentDetails>, close: IModalComponentClose) => void;
}

const PaymentDetailsOverview: IModalComponent<IPaymentDetailsOverviewProps> = ({
    sellerId,
    close,
    readonly = false,
    onSubmit
}) => <ResourceLoader message='Loading payment details...' loader={() => fetchPaymentDetails(sellerId)}>
    {({resource}) => <PaymentDetailsForm close={close} paymentDetails={resource} readonly={readonly}
        onSubmit={onSubmit}/>}
</ResourceLoader>;

export const PaymentsView = () => {
    const ability = useAbility(PaymentsAbility);
    const isSellerOrSelfServe = ability.isSeller() || ability.isSelfServe();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [sellerId, setSellerId] = useState(
        isSellerOrSelfServe ? ability.sellerId() : getSellerId(location.search)
    );
    const [status, setStatus] = useState<PaymentStatus | null>(null);
    const paymentsQuery = usePaymentsQuery(sellerId);
    const payments = paymentsQuery.data ?? [];
    const isPending = paymentsQuery.isLoading;

    const modals = useModals();
  
    const paymentDetails = modals.create(PaymentDetailsOverview, {
        onSubmit: (values, formikActions, close) => {
            updatePaymentDetails({
                ...values,
                sellerId: sellerId
            }).then(close).catch(onSubmitError(formikActions, response => formikActions.setErrors(response.data)));
        }
    });

    useEffect(() => {
        navigate(paymentsLinks.payments(sellerId), { replace: true });
    }, [sellerId]);

    const filteredPayments = useMemo(() => {
        return payments.filter(payment => !status || payment.status === status);
    }, [payments, status]);

    const onPaymentStatusChange = (payment: IPayment, newStatus: PaymentStatus, reason?: string) => {
        updatePaymentStatus(payment.id, newStatus, reason).then(() =>
            queryClient.setQueryData<IPayment[]>(PAYMENTS_QUERY_KEY(sellerId), (previousPayments) =>
                (previousPayments ?? []).map((item) =>
                    item.id === payment.id ? { ...item, status: newStatus, rejectionReason: reason } : item
                )
            )
        );
    };

	    return <div className={style.payment}>
	    <Breadcrumb breadcrumb={{
	        to: paymentsLinks.payments(),
	        title: 'Historical'
	    }}><BreadcrumbsResourcePage title='Historical' toolbar={<>
	            {isSellerOrSelfServe && <Button responsive
	                key='payment-details'
	                disabled={isPending}
	                intent={Intent.WARNING}
                leftIcon={<i className='fa fa-credit-card'/>}
                onClick={() => paymentDetails.show({sellerId})}>
            Payment Details
            </Button>}
            <Button responsive
                intent={Intent.SUCCESS}
                disabled={paymentsQuery.isFetching}
                onClick={() => paymentsQuery.refetch()}
                leftIcon={<i className='fa fa-refresh'/>}
            >Reload</Button>
            {isSellerOrSelfServe && <Link to={paymentsLinks.paymentsTrolley()}>
                <Button>
                    New UI
                </Button>
            </Link>}
	        </>}>
	            <PaymentsTypeNavigation sellerId={sellerId} showTransactionHistory={ability.canViewTransactionHistory()} />
	            <div className="layout-content-box">
                    <form className={style.filters} onSubmit={(e) => e.preventDefault()}>
                        <ToggleGroup
                            ariaLabel="Status"
                    items={[
                        {label: 'All', value: null},
                        {label: 'Pending', value: PaymentStatus.RECEIVED},
                        {label: 'Completed', value: PaymentStatus.PAID},
                        {label: 'Rejected', value: PaymentStatus.REJECTED},
                    ]}
                    value={status}
                    onChange={setStatus}
                    disabled={isPending}
                    className={style.statusFilter}
                        />
                        {!isSellerOrSelfServe && <SelectSeller value={sellerId}
                            onChange={setSellerId}
                            placeholder='Filter by Seller'
                            disabled={isPending}
                            searchable
                            combobox
                            autoFocus
                            clearable
                            className={style.sellerFilter}/>}
                    </form>
	            <TableSort defaultSort={{sort: sortByString, field: 'issuedAt', direction: TableSortDirection.DESC}}
                    withPagination
	                columns={[{
	                    header: 'ID',
	                    cellAttrs: {
                        className: style.paymentId,
                        style: {
                            maxWidth: '120px',
                            overflow: 'hidden'
                        }
                    },
                    small: true,
                    field: 'id',
                    cell: payment => <Copy text={payment.id}>{payment.id}</Copy>
                }, {
                    header: 'Creation date',
                    field: 'issuedAt',
                    cell: payment => stringDateFormat(payment.issuedAt),
                    sort: sortByString
                }, {
                    header: 'Completed date',
                    field: 'completedAt',
                    cell: payment => payment.completedAt ? stringDateFormat(payment.completedAt) : '',
                    sort: sortByString
                }, {
                    header: 'Revenue period',
                    field: 'revenuePeriod',
                    cell: payment => payment.revenuePeriod ? formatInterval(payment.revenuePeriod) : '',
                    sort: sortByInterval,
                }, {
                    header: 'Seller',
                    hide: isSellerOrSelfServe,
                    cell: payment => <NavLink
                        to={clientsLinks.seller(payment.accountId)}>{payment.sellerName}</NavLink>,
                    field: 'sellerName',
                    sort: sortByString
                }, {
                    header: 'Amount, $',
                    cell: payment => renderDollars(payment.amount),
                    field: 'amount',
                    sort: sortByNumber
                }, {
                    header: 'Reason',
                    hide: isSellerOrSelfServe,
                    field: 'rejectionReason',
                    sort: sortByString
                }, {
                    header: 'Status',
                    small: true,
                    field: 'status',
                    sort: sortByString,
                    cell: payment => <PaymentStatusMenu status={payment.status}
                        onChange={(newStatus, reason) =>
                            onPaymentStatusChange(payment, newStatus, reason)}/>
                },
                {
                    small: true,
                    hide: isSellerOrSelfServe,
                    cell: payment => <div style={{display: 'flex', gap: '4px', flexDirection: 'column'}}>
                        <Button small
                            leftIcon={<i className='fa fa-credit-card'/>}
                            onClick={() => paymentDetails.show({
                                sellerId: payment.accountId,
                                readonly: true
                            })}>
                                   Payment Details
                        </Button>
                        <Link to={paymentsLinks.transactionHistory(payment.accountId)}>
                            <Button small
                                leftIcon={<i className='fa fa-history'/>}>
                                Transactions
                            </Button>
                        </Link>
                    </div>
	                }]} header records={filteredPayments}
	                placeholder={isPending && <Progress/>}/>
	            </div>
	        </BreadcrumbsResourcePage></Breadcrumb></div>;
	};
